// Touch controls.
//
// A phone has no pointer lock, no WASD and no hover, so the desktop input path is not "degraded"
// on mobile -- it is absent. This layer supplies the three verbs the game actually needs:
//
//   MOVE    left half of the screen: a stick that SPAWNS WHERE YOUR THUMB LANDS. A fixed stick
//           position is the classic mobile mistake -- thumbs do not land where a designer drew a
//           circle, and every correction costs the player a step of drift.
//   LOOK    right half: drag. Rate is per-CSS-pixel so a 6" phone and a tablet turn the same
//           amount for the same physical swipe.
//   ACT     a button that only exists when there is something to interact with, mirroring the
//           [E] cue exactly -- a button that is always there but usually does nothing teaches
//           people to ignore it.
//
// Sprint has no button: push the stick past 85% and you run. One less thing on screen, and it maps
// to the thing a thumb already does when it wants to go faster.
//
// DOM overlay rather than canvas-drawn: crisp at any devicePixelRatio, costs zero draw calls, and
// the harness can assert on real elements instead of pixels.

const R = 56;          // stick radius in CSS px
const RUN_AT = 0.85;   // fraction of the radius that means "run"

export function hasTouch() {
  return (typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches)
    || ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
}

export class TouchControls {
  constructor(dom) {
    this.dom = dom;
    this.move = { x: 0, z: 0 };
    this.run = false;
    this.lookX = 0;      // accumulated look delta, consumed by the player each frame
    this.lookY = 0;
    this.active = false; // has the user actually touched anything yet
    this.onAct = null;
    this._moveId = null;
    this._lookId = null;
    this._origin = { x: 0, y: 0 };
    this._last = { x: 0, y: 0 };
    this._build();
    this._bind();
  }

  _build() {
    const css = document.createElement('style');
    css.textContent = `
      #nptouch { position: fixed; inset: 0; pointer-events: none; z-index: 60; }
      #npstick { position: absolute; width: ${R * 2}px; height: ${R * 2}px; margin: ${-R}px 0 0 ${-R}px;
        border: 1.5px solid rgba(190,225,255,0.30); border-radius: 50%; opacity: 0;
        transition: opacity 120ms; background: radial-gradient(rgba(120,190,255,0.09), transparent 70%); }
      #npnub { position: absolute; width: 44px; height: 44px; margin: -22px 0 0 -22px; border-radius: 50%;
        background: rgba(190,225,255,0.22); border: 1.5px solid rgba(190,225,255,0.5); opacity: 0;
        transition: opacity 120ms; }
      #npact { position: absolute; right: calc(22px + env(safe-area-inset-right));
        bottom: calc(84px + env(safe-area-inset-bottom)); width: 84px; height: 84px; border-radius: 50%;
        display: grid; place-items: center; text-align: center; pointer-events: auto;
        font: 600 12px/1.25 system-ui, sans-serif; letter-spacing: .04em; color: #d8ecff;
        background: rgba(10,16,26,0.55); border: 1.5px solid rgba(120,200,255,0.45);
        opacity: 0; transform: scale(0.9); transition: opacity 140ms, transform 140ms;
        -webkit-tap-highlight-color: transparent; user-select: none; }
      #npact.on { opacity: 1; transform: scale(1); }
      #npact.push { background: rgba(60,140,220,0.55); }
      #nphint { position: absolute; left: 50%; transform: translateX(-50%);
        bottom: calc(18px + env(safe-area-inset-bottom)); font: 300 11px/1.5 system-ui, sans-serif;
        letter-spacing: .14em; text-transform: uppercase; color: rgba(210,230,255,0.5);
        transition: opacity .5s; white-space: nowrap; }
    `;
    document.head.appendChild(css);
    const root = document.createElement('div');
    root.id = 'nptouch';
    root.innerHTML = '<div id="npstick"></div><div id="npnub"></div>'
      + '<div id="npact">ACT</div>'
      + '<div id="nphint">left thumb walks &nbsp;·&nbsp; right thumb looks</div>';
    document.body.appendChild(root);
    this.stick = root.querySelector('#npstick');
    this.nub = root.querySelector('#npnub');
    this.act = root.querySelector('#npact');
    this.hint = root.querySelector('#nphint');
    this.act.addEventListener('touchstart', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.act.classList.add('push');
      if (this.onAct) this.onAct();
    }, { passive: false });
    const release = () => this.act.classList.remove('push');
    this.act.addEventListener('touchend', release);
    this.act.addEventListener('touchcancel', release);
  }

  /** Show/hide the action button and its label; mirrors the desktop [E] cue. */
  setAction(label) {
    if (label) {
      this.act.textContent = label;
      this.act.classList.add('on');
    } else {
      this.act.classList.remove('on');
    }
  }

  _bind() {
    const opts = { passive: false };
    this.dom.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.active = true;
      if (this.hint) { this.hint.style.opacity = '0'; }
      for (const t of e.changedTouches) {
        const left = t.clientX < innerWidth * 0.5;
        if (left && this._moveId === null) {
          this._moveId = t.identifier;
          this._origin = { x: t.clientX, y: t.clientY };
          this.stick.style.left = `${t.clientX}px`;
          this.stick.style.top = `${t.clientY}px`;
          this.stick.style.opacity = '1';
          this.nub.style.left = `${t.clientX}px`;
          this.nub.style.top = `${t.clientY}px`;
          this.nub.style.opacity = '1';
        } else if (!left && this._lookId === null) {
          this._lookId = t.identifier;
          this._last = { x: t.clientX, y: t.clientY };
        }
      }
    }, opts);

    this.dom.addEventListener('touchmove', (e) => {
      e.preventDefault();
      for (const t of e.changedTouches) {
        if (t.identifier === this._moveId) {
          let dx = t.clientX - this._origin.x;
          let dy = t.clientY - this._origin.y;
          const d = Math.hypot(dx, dy);
          const clamped = Math.min(d, R);
          if (d > 0.001) { dx = (dx / d) * clamped; dy = (dy / d) * clamped; }
          this.nub.style.left = `${this._origin.x + dx}px`;
          this.nub.style.top = `${this._origin.y + dy}px`;
          // Normalised to the radius. Screen-up is -y and forward is -z, so z maps straight over.
          this.move.x = dx / R;
          this.move.z = dy / R;
          this.run = clamped / R > RUN_AT;
        } else if (t.identifier === this._lookId) {
          this.lookX += t.clientX - this._last.x;
          this.lookY += t.clientY - this._last.y;
          this._last = { x: t.clientX, y: t.clientY };
        }
      }
    }, opts);

    const end = (e) => {
      for (const t of e.changedTouches) {
        if (t.identifier === this._moveId) {
          this._moveId = null;
          this.move.x = 0; this.move.z = 0; this.run = false;
          this.stick.style.opacity = '0';
          this.nub.style.opacity = '0';
        } else if (t.identifier === this._lookId) {
          this._lookId = null;
        }
      }
    };
    this.dom.addEventListener('touchend', end, opts);
    this.dom.addEventListener('touchcancel', end, opts);
  }

  /** Take the accumulated look delta and reset it. Called once per frame by the player. */
  consumeLook() {
    const x = this.lookX, y = this.lookY;
    this.lookX = 0; this.lookY = 0;
    return { x, y };
  }
}
